
// Helper function for Project Filtering logic
export const isItemInProject = (activeProject: string, allData: any, projName?: string, epochId?: number) => {
    if (!activeProject || activeProject === 'all') return true;

    const activeProjectNormalized = activeProject.toLowerCase().replace(/_/g, ' ').trim();
    const itemProjectNormalized = (projName || '').toLowerCase().replace(/_/g, ' ').trim();

    if (itemProjectNormalized) {
        if (itemProjectNormalized === activeProjectNormalized) return true;
        if (itemProjectNormalized.includes(activeProjectNormalized) || activeProjectNormalized.includes(itemProjectNormalized)) return true;
    }

    if (epochId) {
        const epoch = allData.iterationHistory?.find((e: any) => (e.epochNumber || e.iteration) === epochId);
        if (epoch) {
            const epochName = epoch.epochName || epoch.iterationName || '';
            const epochProject = epochName.includes(' - ') ? epochName.split(' - ')[0] : epochName;
            const epochProjectNormalized = epochProject.toLowerCase().replace(/_/g, ' ').trim();
            if (epochProjectNormalized === activeProjectNormalized || epochProjectNormalized.includes(activeProjectNormalized)) {
                return true;
            }
        }
    }

    if (!projName && !epochId) {
        return activeProjectNormalized === 'aerator pro';
    }
    return false;
};

// --- SIMPLE FINDERS (Low Complexity) ---

export const findHighestRiskNew = (allData: any): string => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0) return "No FMEAs to analyze yet. Create some first!";

    const sorted = [...fmeas].sort((a: any, b: any) => (b.S * b.O * b.D) - (a.S * a.O * a.D));
    const highest = sorted[0];
    const rpn = highest.S * highest.O * highest.D;

    let response = `🚨 **Highest Risk Item: "${highest.failureMode}"**\n\n`;
    response += `📊 **RPN: ${rpn}** (Severity ${highest.S} × Occurrence ${highest.O} × Detection ${highest.D})\n\n`;
    response += `📍 Function: ${highest.itemFunction}\n💥 Effects: ${highest.effects}\n\n`;
    response += rpn > 100
        ? `⚠️ **Action Required:** RPN > 100 is critical. Focus on reducing Occurrence or Detection first.`
        : `✅ This is your highest risk, but it's manageable (RPN < 100).`;
    return response;
};

export const findHighestDetectionNew = (allData: any): string => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0) return "No FMEAs to analyze yet.";

    const highest = [...fmeas].sort((a: any, b: any) => b.D - a.D)[0];
    const rpn = highest.S * highest.O * highest.D;
    const detectability = highest.D >= 9 ? 'Almost impossible to detect' : highest.D >= 7 ? 'Hard to detect' : 'detectable';

    return `🔍 **Hardest to Detect: "${highest.failureMode}"**\n\n` +
        `📉 Detection Score: ${highest.D}/10 (${detectability})\n\n` +
        `This high detection score drives the RPN up to ${rpn}.\n\n` +
        `💡 **Recommendation:** Add inspection points, sensors, or tests in DVP&R to improve detectability.`;
};

export const findHighestSeverity = (allData: any): string => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0) return "No FMEAs to analyze yet.";

    const highest = [...fmeas].sort((a: any, b: any) => b.S - a.S)[0];
    const rpn = highest.S * highest.O * highest.D;
    const severityDesc = highest.S >= 9 ? 'Catastrophic' : highest.S >= 7 ? 'Very serious' : 'Serious';

    return `**Most Severe: "${highest.failureMode}"**\n\n` +
        `🚨 Severity: ${highest.S}/10 (${severityDesc})\n\n` +
        `💥 Effects: ${highest.effects}\n` +
        `📊 RPN = ${rpn} (S=${highest.S}, O=${highest.O}, D=${highest.D})\n\n` +
        `⚠️ **Critical:** Severity ${highest.S} can only be reduced by redesign or elimination.`;
};

export const findHighestOccurrence = (allData: any): string => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0) return "No FMEAs to analyze yet.";

    const highest = [...fmeas].sort((a: any, b: any) => b.O - a.O)[0];
    const rpn = highest.S * highest.O * highest.D;
    const likelihood = highest.O >= 8 ? 'Almost certain' : highest.O >= 6 ? 'Very likely' : 'Likely';

    let response = `**Most Likely to Occur: "${highest.failureMode}"**\n\n`;
    response += `📈 Occurrence: ${highest.O}/10 (${likelihood})\n\n`;
    response += `📊 RPN = ${rpn}\n📍 Function: ${highest.itemFunction}\n\n`;

    if (highest.preventionControls?.length > 0) {
        response += `🛡️ Current Prevention Controls:\n` + highest.preventionControls.join(', ') + `\n💡 Consider stronger prevention.`;
    } else {
        response += `❌ **No prevention controls!** Add prevention measures.`;
    }
    return response;
};

// --- CONTROL & TABLE HELPERS ---

export const findControlsFor = (query: string, allData: any): string | null => {
    const fmeas = allData?.fmeaItems || [];
    const lowerQuery = query.toLowerCase();
    const commonWords = ['what', 'are', 'the', 'control', 'controls', 'for', 'about', 'on', 'is'];
    const terms = lowerQuery.split(' ').filter(word => !commonWords.includes(word) && word.length > 2);

    for (const term of terms) {
        const found = fmeas.find((f: any) =>
            f.failureMode.toLowerCase().includes(term) ||
            f.effects.toLowerCase().includes(term) ||
            f.itemFunction.toLowerCase().includes(term)
        );

        if (found) {
            const rpn = found.S * found.O * found.D;
            let response = `**Controls for: "${found.failureMode}"**\n\n`;

            const hasPrev = found.preventionControls?.length > 0;
            const hasDet = found.detectionControls?.length > 0;

            if (hasPrev) response += `🛡️ **Prevention:**\n${found.preventionControls.join('\n• ')}\n\n`;
            if (hasDet) response += `🔍 **Detection:**\n${found.detectionControls.join('\n• ')}\n\n`;

            if (!hasPrev && !hasDet) {
                response += `❌ No controls defined yet.\n\n**Recommendation:** Add controls to reduce RPN ${rpn}.`;
            } else {
                response += `📊 Current RPN: ${rpn}`;
            }
            return response;
        }
    }
    return null;
};

export const answerTableQuestion = (query: string, allData: any): string | null => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0 && query.includes('count')) return "You don't have any FMEAs yet.";

    if (query.toLowerCase().includes('how many')) {
        const high = fmeas.filter((f: any) => (f.S * f.O * f.D) > 100).length;
        const low = fmeas.length - high;
        return `📊 **FMEA Summary:**\nTotal: ${fmeas.length}\n🔴 High Risk (>100): ${high}\n🟢 Low Risk: ${low}`;
    }
    // ... simplified - other cases handled by findAnswer fallbacks or simple logic
    return null;
};

// --- PAGE ANALYSIS (Broken Down) ---

const analyzeDraftMode = (targetItems: any[], activeCount: number) => {
    if (targetItems.length === 0) return `📝 **Draft Mode:** No drafts found. ${activeCount} active items exist.`;

    const missingData = targetItems.filter((f: any) => !f.itemFunction || !f.requirement || !f.preventionControls?.length);
    const highRisk = targetItems.filter((f: any) => (f.S * f.O * f.D) > 100);

    let report = `📝 **Draft Review (${targetItems.length} items):**\n\n`;
    if (missingData.length > 0) report += `⚠️ ${missingData.length} need more detail (Function/Req/Controls).\n`;
    if (highRisk.length > 0) report += `🚨 ${highRisk.length} are High Risk (RPN > 100).\n`;

    report += `\n**Next Steps:** Review completeness and approve drafts.`;
    return report;
};

const analyzeActiveMode = (targetItems: any[]) => {
    if (targetItems.length === 0) return "No active FMEAs found for this project.";

    const highRPN = targetItems.filter((f: any) => (f.S * f.O * f.D) > 100);
    const safety = targetItems.filter((f: any) => f.S >= 8);
    const avgRPN = targetItems.reduce((acc, f) => acc + (f.S * f.O * f.D), 0) / targetItems.length;

    let report = `**Active FMEA Analysis:**\n\n`;
    report += `📊 Average RPN: ${avgRPN.toFixed(1)}\n`;
    if (highRPN.length > 0) report += `⚠️ **${highRPN.length} High Risk Items** need attention.\n`;
    if (safety.length > 0) report += `🚨 **${safety.length} Safety Critical** items (S≥8).\n`;

    return report + (highRPN.length > 0 ? "\nFocus on high RPN items first." : "\nGreat job keeping risks low!");
};

const analyzeFmeaPage = (allData: any, activeProject: string, location: any) => {
    const allFmeas = allData?.fmeaItems || [];
    const projectFmeas = allFmeas.filter((f: any) => isItemInProject(activeProject, allData, f.projectName, f.epochId));

    const viewState = location.state as any;
    const isDraftMode = viewState?.view === 'draft' || window.location.href.includes('draft');

    const targetItems = isDraftMode
        ? projectFmeas.filter((f: any) => (f.status || 'draft') === 'draft')
        : projectFmeas.filter((f: any) => (f.status || 'draft') === 'active');

    return isDraftMode
        ? analyzeDraftMode(targetItems, projectFmeas.filter((f: any) => (f.status || 'draft') === 'active').length)
        : analyzeActiveMode(targetItems);
};

export const analyzeCurrentPageData = (allData: any, location: any, activeProject: string): string => {
    const path = location.pathname;

    if (path === '/fmeas' || path === '/fmea-editor') return analyzeFmeaPage(allData, activeProject, location);

    if (path === '/dashboard') {
        const fmeas = allData?.fmeaItems || [];
        const highRisk = fmeas.filter((f: any) => (f.S * f.O * f.D) > 100).length;
        return `**Dashboard Health:**\n${fmeas.length} total items.\n${highRisk} high risk items.\nOverall Status: ${highRisk > 0 ? '⚠️ Attention Needed' : '✅ Healthy'}`;
    }

    if (path === '/materials-database') {
        const materials = allData?.materials || [];
        const validated = materials.filter((m: any) => m.validated).length;
        return `**Materials:** ${validated}/${materials.length} validated.`;
    }

    return "Navigate to FMEA, Dashboard, or Materials pages for specific analysis.";
};

export const getSpecificRecommendations = (allData: any): string => {
    const fmeas = allData?.fmeaItems || [];
    if (fmeas.length === 0) return "Create FMEAs first.";

    const top3 = [...fmeas].sort((a: any, b: any) => (b.S * b.O * b.D) - (a.S * a.O * a.D)).slice(0, 3);

    let recs = "**Top Priorities:**\n";
    top3.forEach((f: any, i) => {
        recs += `${i + 1}. "${f.failureMode}" (RPN: ${f.S * f.O * f.D})\n`;
        if (f.S >= 8) recs += "   → High Severity: Redesign required.\n";
        else if (f.O >= 7) recs += "   → High Occurrence: Improve prevention.\n";
        else if (f.D >= 7) recs += "   → Poor Detection: Add DVP&R tests.\n";
    });
    return recs;
};

export const findProblemsOnPage = (allData: any, location: any): string => {
    if (location.pathname === '/fmeas' || location.pathname === '/fmea-editor') {
        const fmeas = allData?.fmeaItems || [];
        const critical = fmeas.filter((f: any) => f.S >= 9 && (f.S * f.O * f.D) > 80);
        const noControls = fmeas.filter((f: any) => !f.preventionControls?.length && !f.detectionControls?.length);

        if (critical.length === 0 && noControls.length === 0) return "No major problems found!";

        let report = "**Problems Found:**\n";
        if (critical.length > 0) report += `🚨 ${critical.length} Critical Safety Issues (S≥9, RPN>80)\n`;
        if (noControls.length > 0) report += `❌ ${noControls.length} items have NO controls defined.\n`;
        return report;
    }
    return "Navigate to FMEA pages to find problems.";
};

// --- ITEM LOOKUP (Broken Down) ---

const lookupFmeaItem = (terms: string[], fmeas: any[]) => {
    for (const term of terms) {
        const found = fmeas.find((f: any) =>
            f.failureMode.toLowerCase().includes(term) || f.function.toLowerCase().includes(term)
        );
        if (found) {
            const rpn = found.S * found.O * found.D;
            return `**Found FMEA:** "${found.failureMode}"\nRPN: ${rpn}\nFunction: ${found.itemFunction}`;
        }
    }
    return null;
};

const lookupBomItem = (terms: string[], bomItems: any[]) => {
    for (const term of terms) {
        const found = bomItems.find((b: any) => b.component.toLowerCase().includes(term));
        if (found) return `**Found Component:** ${found.component}\nFunction: ${found.basicFunction}`;
    }
    return null;
};

export const lookupItem = (query: string, allData: any, location: any): string | null => {
    const terms = query.toLowerCase().split(' ').filter(w => w.length > 3);
    const path = location.pathname;

    if (path.includes('fmea')) return lookupFmeaItem(terms, allData?.fmeaItems || []);
    if (path.includes('pbs') || path.includes('bom')) return lookupBomItem(terms, allData?.bomItems || []);

    return null;
};
